import { supabase } from '../lib/supabase'
import {
  ApiResponse,
  HelpdeskModule,
  HelpdeskPermission,
  HelpdeskPermissionWithDetails,
  HelpdeskUserGroup,
  CreateHelpdeskPermissionData,
  CreateHelpdeskUserGroupData,
  UpdateHelpdeskPermissionData,
  UpdateHelpdeskUserGroupData
} from '../types'

export const helpdeskPermissionsService = {
  async getGroups(): Promise<ApiResponse<HelpdeskUserGroup[]>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_user_groups')
        .select('*')
        .order('display_name', { ascending: true })

      if (error) throw error
      return { data: (data || []) as HelpdeskUserGroup[], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  async createGroup(payload: CreateHelpdeskUserGroupData): Promise<ApiResponse<HelpdeskUserGroup>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_user_groups')
        .insert(payload)
        .select()
        .single()

      if (error) throw error
      return { data: data as HelpdeskUserGroup, success: true }
    } catch (error) {
      return { data: {} as HelpdeskUserGroup, error: (error as Error).message, success: false }
    }
  },

  async updateGroup(id: string, payload: UpdateHelpdeskUserGroupData): Promise<ApiResponse<HelpdeskUserGroup>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_user_groups')
        .update(payload)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return { data: data as HelpdeskUserGroup, success: true }
    } catch (error) {
      return { data: {} as HelpdeskUserGroup, error: (error as Error).message, success: false }
    }
  },

  async deleteGroup(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('helpdesk_user_groups')
        .delete()
        .eq('id', id)

      if (error) throw error
      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  },

  async getActiveModules(): Promise<ApiResponse<HelpdeskModule[]>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_modules')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true })

      if (error) throw error
      return { data: (data || []) as HelpdeskModule[], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  async getPermissionsByGroup(groupId: string): Promise<ApiResponse<HelpdeskPermissionWithDetails[]>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_permissions')
        .select(`
          *,
          module:helpdesk_modules(*),
          helpdesk_user_group:helpdesk_user_groups(*)
        `)
        .eq('helpdesk_user_group_id', groupId)

      if (error) throw error
      return { data: (data || []) as HelpdeskPermissionWithDetails[], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  async getAllPermissions(): Promise<ApiResponse<HelpdeskPermission[]>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_permissions')
        .select('*')

      if (error) throw error
      return { data: (data || []) as HelpdeskPermission[], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  async upsertPermission(
    payload: CreateHelpdeskPermissionData | UpdateHelpdeskPermissionData
  ): Promise<ApiResponse<HelpdeskPermission>> {
    try {
      const { data, error } = await supabase
        .from('helpdesk_permissions')
        .upsert(payload, { onConflict: 'helpdesk_user_group_id,module_id' })
        .select()
        .single()

      if (error) throw error
      return { data: data as HelpdeskPermission, success: true }
    } catch (error) {
      return { data: {} as HelpdeskPermission, error: (error as Error).message, success: false }
    }
  }
}
